/**
 * 窄槽枚举 → 人话(2026-08-02,条 3)。
 *
 * ═══ 这份表和 ai 那份是什么关系 ═══════════════════════════════════════════════
 * **值域**只有一份 —— ai `models.py` 的 `AssetShotType` / `AssetAngle` /
 * `AssetNegativeSpace` 与 `style_tokens.py` 的 `Lighting` / `Material` / `Tone`,
 * 前端 `canvas.ts` 逐字对齐。这份表只管**措辞**(界面上的下拉选项文案 +
 * `props.prompt` 那句给人看的话)。
 *
 * ★所以「两处各写各的」这条老病在这里不成立:对齐的是值不是措辞,
 *   而值一旦漂了,ai 侧 pydantic 会当场拒收(不是静默吞掉)。
 * ★真正发给引擎的画面指令由 **ai 侧**按槽编译(`orchestrator._slot_prompt_parts`);
 *   这里生成的那句话是给**用户看**的,顺带进 `blockDigest` ——
 *   两者措辞不必逐字相同,但**内容必须同源**(都只读这几个槽)。
 */
import type {
  AssetAngle,
  AssetNegativeSpace,
  AssetShotType,
  ImageSlots,
  Lighting,
  Material,
  StyleTone,
} from "./canvas";

export const SHOT_WORDS: Record<AssetShotType, string> = {
  hero: "主图正面全景",
  angle: "多角度展示",
  detail: "局部特写",
  texture: "质地微距",
  lifestyle: "生活场景",
  flatlay: "平铺俯拍",
  inhand: "手持(带比例参照)",
  concept: "概念意象",
  other: "常规取景",
};

export const ANGLE_WORDS: Record<AssetAngle, string> = {
  front: "正面",
  side: "侧面",
  back: "背面",
  top: "顶视",
  "45": "45 度俯视",
  closeup: "贴近特写",
  unknown: "",
};

export const LIGHTING_WORDS: Record<Lighting, string> = {
  studio: "影棚布光",
  natural: "自然光",
  dramatic: "戏剧性光影",
  soft: "柔光",
};

export const MATERIAL_WORDS: Record<Material, string> = {
  matte: "哑光质感",
  glossy: "光泽质感",
  frosted: "磨砂质感",
  metallic: "金属质感",
  natural: "自然材质",
};

export const TONE_WORDS: Record<StyleTone, string> = {
  premium_minimal: "高端极简调性",
  vivid_promo: "活力促销调性",
  warm_life: "温暖生活调性",
  tech_cool: "科技冷调",
  fresh_clean: "清新干净调性",
};

export const NEGATIVE_SPACE_WORDS: Record<AssetNegativeSpace, string> = {
  none: "不特意留白",
  top: "画面上方留白",
  bottom: "画面下方留白",
  left: "画面左侧留白",
  right: "画面右侧留白",
  center: "画面中部留白",
};

/**
 * 五格 + 自由口 → 给**用户看**的那句话(也是 digest 的一部分)。
 *
 * ★空格子整段不出现 —— 与 ai `_slot_prompt_parts` 同一条口径:
 *   写「环境:无」是编出来的话。
 */
export function slotSentence(s: ImageSlots): string[] {
  const parts: string[] = [];
  if (s.subject?.trim()) parts.push(s.subject.trim());
  const framing = [
    s.shotType ? SHOT_WORDS[s.shotType] : "",
    s.angle ? ANGLE_WORDS[s.angle] : "",
  ].filter(Boolean);
  if (framing.length) parts.push(framing.join("、"));
  if (s.environment?.trim()) parts.push(s.environment.trim());
  const light = [
    s.lighting ? LIGHTING_WORDS[s.lighting] : "",
    s.material ? MATERIAL_WORDS[s.material] : "",
    s.tone ? TONE_WORDS[s.tone] : "",
  ].filter(Boolean);
  if (light.length) parts.push(light.join("、"));
  // none 是「不特意留白」,说出来只会变成一条要模型满足的要求 → 不进句子
  if (s.negativeSpace && s.negativeSpace !== "none") {
    parts.push(NEGATIVE_SPACE_WORDS[s.negativeSpace]);
  }
  if (s.extra?.trim()) parts.push(s.extra.trim());
  return parts;
}

/**
 * 折叠态那一行摘要(2026-08-05,D3)——「除了你写的那段话,还补了什么」。
 *
 * ★**刻意不含 subject**:主体就是折叠外面那个文本域里的内容,再复述一遍等于把同一句话
 *   在同一屏说两遍。这行只回答「AI 替我补了哪些细节」。
 * ★封顶 3 项:它是一行灰字提示,不是清单;想看全的点开精调。
 */
export function slotDigestLine(s: ImageSlots): string {
  const bits: string[] = [];
  if (s.environment?.trim()) bits.push(`环境·${s.environment.trim()}`);
  if (s.lighting) bits.push(`光影·${LIGHTING_WORDS[s.lighting]}`);
  if (s.material) bits.push(`材质·${MATERIAL_WORDS[s.material]}`);
  if (s.tone) bits.push(`调性·${TONE_WORDS[s.tone]}`);
  if (s.shotType) bits.push(`取景·${SHOT_WORDS[s.shotType]}`);
  if (s.extra?.trim()) bits.push(`还有·${s.extra.trim()}`);
  return bits.slice(0, 3).join(" · ");
}
